import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import Loadable from 'react-loadable';

import { RouteComponentProps } from 'src/router/types';
import { MenuItem } from 'src/components/navigation';
import Navigation, {
  useLegacyReduxNavigationData,
} from 'src/sections/global/containers/navigation-v2';
import { memoRepoLink } from 'src/sections/repository/components/navigation-repo-link';
import {
  getCurrentRepository,
  getCurrentRepositoryLinkData,
  getIsCurrentRepositoryPrivate,
} from 'src/selectors/repository-selectors';
import { BucketState } from 'src/types/state';
import { useRepositoryNavigationProps } from './use-repository-navigation-props';
import { useRepositoryNavigationEffects } from './use-repository-navigation-effects';

const RepositoryNavigationNext = Loadable({
  loading: () => null,
  loader: () =>
    import(
      /* webpackChunkName: "navigation-next-repository" */
      'src/sections/repository/components/repository-navigation-next',
      // @ts-ignore
      { preload: true }
    ),
});

type RepositoryNavigationProps = RouteComponentProps & {
  children: React.ReactNode;
  isBeingRenderedInsideMobileNav: boolean;
  menuItems: MenuItem[];
  selectedMenuItem: MenuItem | undefined;
};

export const RepositoryNavigation = React.memo(
  (props: RepositoryNavigationProps) => {
    useRepositoryNavigationEffects();

    const navigationData = useLegacyReduxNavigationData();

    const currentRepositoryLinkData = useSelector(
      getCurrentRepositoryLinkData,
      shallowEqual
    );
    const isCurrentRepositoryPrivate = useSelector(
      getIsCurrentRepositoryPrivate
    );
    const containerBitbucketActions = useSelector(
      (state: BucketState) => state.repository.section.bitbucketActions
    );
    const containerConnectActions = useSelector(
      (state: BucketState) => state.repository.section.connectActions
    );
    const { name, links, landingPage, fullName } = currentRepositoryLinkData;
    const htmlUrl = links?.html.href;
    const avatarUrl = links?.avatar.href;
    const hasRepo = !!name && !!fullName && !!htmlUrl;
    const RepoLink: React.FunctionComponent<any> = memoRepoLink(
      name,
      fullName,
      landingPage,
      htmlUrl
    );
    const headerLinkComponent = hasRepo ? RepoLink : undefined;

    const { repoName, repoSlug } = useSelector((state: BucketState) => {
      const currentRepository = getCurrentRepository(state);
      return {
        repoName: currentRepository ? currentRepository.name : undefined,
        repoSlug: currentRepository ? currentRepository.slug : undefined,
      };
    }, shallowEqual);

    return (
      <Navigation
        {...navigationData}
        isBeingRenderedInsideMobileNav={props.isBeingRenderedInsideMobileNav}
        containerBitbucketActions={containerBitbucketActions}
        containerConnectActions={containerConnectActions}
        containerName={name}
        containerHref={htmlUrl}
        containerLogo={avatarUrl}
        createDrawerHeaderLinkComponent={headerLinkComponent}
        isGlobalContext={false}
        isPrivate={isCurrentRepositoryPrivate}
        navigationType="container"
        containerNavigationComponent={RepositoryNavigationNext}
        repoName={repoName}
        repoSlug={repoSlug}
        useContainerNavigationProps={useRepositoryNavigationProps}
      >
        {props.children}
      </Navigation>
    );
  }
);
